import { analyzeAndGroupCables, getCableDisplayName } from './cableLogic';

const getFirstIndex = (cables = []) => {
  if (!cables.length) return Infinity;
  return Math.min(...cables.map(c => c.originalIndex ?? 0));
};

// ==================== FUNZIONI STIMA ALTEZZA ====================

/**
 * Stima l'altezza in mm di un nodo sheath per l'esportazione PDF
 * 
 * IMPORTANTE: Segue la stessa logica di HybridCableVisualization:
 * - Cavi multipolari → SVG separato per ognuno
 * - Cavi multipli (stessa siglatura) → SVG condiviso (MultipleCableVisualization)
 * - Cavi singoli CON guaina → TUTTI condividono UN UNICO SVG (SheathCableVisualization)
 * - Cavi singoli SENZA guaina → SVG separato per ognuno
 */
const estimateSheathNodeHeight = (groupList, settings) => {
  const pageWidth = 210; // A4 width in mm
  const margin = 15;
  const usableWidth = pageWidth - (2 * margin);
  const scale = (usableWidth - 6) / 800;

  let height = 12; // Header colorato del nodo

  // Separa i gruppi come fa HybridCableVisualization
  const multipolarCables = [];
  const multipleGroups = [];
  const singleCablesWithSheath = [];

  groupList.forEach(group => {
    if (group.isSingle) {
      const cable = group.cables[0];
      if (cable.poli && Array.isArray(cable.poli)) {
        // Cavo multipolare
        multipolarCables.push(cable);
      } else {
        // Cavo singolo (dentro la guaina, condivide l'SVG con altri singoli)
        singleCablesWithSheath.push(cable);
      }
    } else {
      // Gruppo di cavi multipli
      multipleGroups.push(group);
    }
  });

  // 1. Calcola altezza per ogni cavo multipolare (ognuno ha il suo SVG)
  multipolarCables.forEach(cable => {
    const svgHeight = Math.max(200, 80 + (cable.poli.length * 60));
    height += (svgHeight * scale) + 4; // SVG
    height += 8 + 6 + (cable.poli.length * 10) + 2; // Dettagli tabella poli
  });

  // 2. Calcola altezza per ogni gruppo multiplo (ogni gruppo ha il suo SVG)
  multipleGroups.forEach(group => {
    const svgHeight = 250;
    height += (svgHeight * scale) + 4; // SVG condiviso dal gruppo
    height += group.cables.length * 8; // Dettagli per ogni cavo
  });

  // 3. Calcola altezza per i cavi singoli nella guaina (TUTTI condividono UN UNICO SVG)
  if (singleCablesWithSheath.length > 0) {
    // L'SVG della guaina cresce con il numero di cavi - FORMULA RIDOTTA
    const svgHeight = Math.max(160, 40 + (singleCablesWithSheath.length * 50));
    height += (svgHeight * scale) + 4; // Un solo SVG per tutti i cavi singoli
    height += singleCablesWithSheath.length * 8; // Dettagli per ogni cavo
  }

  return height;
};

/**
 * Stima l'altezza di un elemento renderizzabile (per la divisione)
 * Ogni elemento corrisponde a un SVG nel rendering finale
 */
const estimateRenderableHeight = (renderable, settings) => {
  const pageWidth = 210;
  const margin = 15;
  const usableWidth = pageWidth - (2 * margin);
  const scale = (usableWidth - 6) / 800;

  if (renderable.type === 'multipolar') {
    const cable = renderable.cable;
    const svgHeight = Math.max(200, 80 + (cable.poli.length * 60));
    return (svgHeight * scale) + 4 + 8 + 6 + (cable.poli.length * 10) + 2;
  }

  if (renderable.type === 'multiple') {
    const svgHeight = 250;
    return (svgHeight * scale) + 4 + (renderable.cables.length * 8);
  }

  if (renderable.type === 'sheathSingles') {
    // Tutti i cavi singoli nella guaina condividono un SVG - FORMULA RIDOTTA
    const svgHeight = Math.max(160, 40 + (renderable.cables.length * 50));
    return (svgHeight * scale) + 4 + (renderable.cables.length * 8);
  }

  return 0;
};

// ==================== FUNZIONI COSTRUZIONE NODI ====================

const buildSingleNode = (cable, level) => ({
  id: `single-${cable.originalIndex}`,
  level,
  type: 'single',
  cables: [cable],
  firstIndex: cable.originalIndex ?? 0,
  subGroups: []
});

const buildMultipleNode = (group, level, settings) => {
  const firstCable = group.cables[0];
  const label = getCableDisplayName(firstCable, settings?.lingua, settings);
  const baseId = group.key || `multiple-${firstCable.originalIndex}`;

  return {
    id: baseId,
    level,
    type: 'multiple',
    label,
    cables: group.cables,
    firstIndex: getFirstIndex(group.cables),
    subGroups: group.cables.map(cable => buildSingleNode(cable, level + 1))
  };
};

/**
 * Costruisce un singolo nodo sheath dai gruppi logici
 */
const buildSheathNode = (sheathId, groupList, level, settings, subgroupIndex = null, totalSubgroups = null) => {
  const allCables = groupList.flatMap(group => group.cables);
  const subGroups = groupList.map(group =>
    group.isSingle
      ? buildSingleNode(group.cables[0], level + 1)
      : buildMultipleNode(group, level + 1, settings)
  );

  const suffix = subgroupIndex !== null ? `-${subgroupIndex}` : '';
  const id = `sheath-${sheathId}-${level}${suffix}`;

  return {
    id, // ID Deterministico e stabile
    level,
    type: 'sheath',
    sheathId,
    label: `Guaina ${sheathId}`,
    cables: allCables,
    firstIndex: getFirstIndex(allCables),
    subGroups,
    subgroupIndex,
    totalSubgroups
  };
};

/**
 * Divide un nodo sheath in sottogruppi se supera l'altezza massima della pagina
 * 
 * La divisione avviene per "renderables" - elementi che corrispondono a SVG separati:
 * - Ogni cavo multipolare è un renderable
 * - Ogni gruppo multiplo è un renderable
 * - TUTTI i cavi singoli insieme formano UN SOLO renderable (l'SVG della guaina)
 */
const divideSheathIntoSubgroups = (sheathId, groupList, level, settings, dynamicGroupsDivision) => {
  // Se la divisione dinamica non è attiva, ritorna un singolo nodo
  if (!dynamicGroupsDivision) {
    return [buildSheathNode(sheathId, groupList, level, settings)];
  }

  const MAX_HEIGHT = 220; // Altezza massima in mm per pagina
  const HEADER_HEIGHT = 12; // Altezza dell'header del gruppo

  // Stima l'altezza totale del nodo sheath
  const totalHeight = estimateSheathNodeHeight(groupList, settings);

  // Se entra in una pagina, ritorna un singolo nodo senza numerazione
  if (totalHeight <= MAX_HEIGHT) {
    return [buildSheathNode(sheathId, groupList, level, settings)];
  }

  // Converte i gruppi in "renderables" (elementi che corrispondono a SVG)
  const renderables = [];
  const singleCablesGroups = []; // Gruppi di cavi singoli (vanno tenuti insieme)

  groupList.forEach(group => {
    if (group.isSingle) {
      const cable = group.cables[0];
      if (cable.poli && Array.isArray(cable.poli)) {
        // Cavo multipolare → SVG separato
        renderables.push({
          type: 'multipolar',
          cable,
          groups: [group],
          firstIndex: cable.originalIndex ?? 0
        });
      } else {
        // Cavo singolo → va raggruppato con altri singoli
        singleCablesGroups.push(group);
      }
    } else {
      // Gruppo multiplo → SVG separato
      renderables.push({
        type: 'multiple',
        cables: group.cables,
        groups: [group],
        firstIndex: getFirstIndex(group.cables)
      });
    }
  });

  // Aggiungi tutti i cavi singoli come un unico renderable (condividono l'SVG)
  if (singleCablesGroups.length > 0) {
    const allSingleCables = singleCablesGroups.map(g => g.cables[0]);
    renderables.push({
      type: 'sheathSingles',
      cables: allSingleCables,
      groups: singleCablesGroups,
      firstIndex: getFirstIndex(allSingleCables)
    });
  }

  // Ordina per firstIndex
  renderables.sort((a, b) => a.firstIndex - b.firstIndex);

  // Dividi i renderables in sottogruppi basati sull'altezza
  const subNodes = [];
  let currentRenderables = [];
  let currentHeight = HEADER_HEIGHT;
  let subgroupIndex = 1;

  for (const renderable of renderables) {
    const renderableHeight = estimateRenderableHeight(renderable, settings);

    // Se aggiungere questo renderable supera il limite E abbiamo già dei renderables
    if (currentHeight + renderableHeight > MAX_HEIGHT && currentRenderables.length > 0) {
      // Salva il sottogruppo corrente
      subNodes.push({
        renderables: [...currentRenderables],
        subgroupIndex: subgroupIndex++
      });

      // Inizia un nuovo sottogruppo
      currentRenderables = [];
      currentHeight = HEADER_HEIGHT;
    }

    currentRenderables.push(renderable);
    currentHeight += renderableHeight;
  }

  // Aggiungi l'ultimo sottogruppo
  if (currentRenderables.length > 0) {
    subNodes.push({
      renderables: currentRenderables,
      subgroupIndex: subgroupIndex
    });
  }

  const totalSubgroups = subNodes.length;

  // Costruisci i nodi sheath con la numerazione x/y
  return subNodes.map(sub => {
    // Ricostruisci i gruppi dai renderables
    const groups = sub.renderables.flatMap(r => r.groups);
    return buildSheathNode(sheathId, groups, level, settings, sub.subgroupIndex, totalSubgroups);
  });
};

/**
 * Costruisce i sottogruppi per un insieme di cavi
 * Supporta la divisione dinamica per i nodi sheath
 */
const buildSubGroups = (cables = [], level, settings, options = { allowSheath: true, dynamicGroupsDivision: false }) => {
  if (!cables || cables.length === 0) return [];

  const groups = analyzeAndGroupCables(cables, settings);
  const sheathBuckets = new Map();
  const regularGroups = [];

  groups.forEach(group => {
    const groupCables = group.cables || [];
    const sheathIds = Array.from(new Set(
      groupCables
        .filter(cable => Boolean(cable.guaina))
        .map(cable => cable.guaina)
    ));

    const canAttachToSheath =
      options.allowSheath &&
      sheathIds.length === 1;

    if (canAttachToSheath) {
      const sheathId = sheathIds[0];
      if (!sheathBuckets.has(sheathId)) {
        sheathBuckets.set(sheathId, []);
      }
      sheathBuckets.get(sheathId).push(group);
    } else {
      regularGroups.push(group);
    }
  });

  const resultNodes = [];

  // Per ogni guaina, verifica se deve essere divisa in sottogruppi
  sheathBuckets.forEach((groupList, sheathId) => {
    const sheathNodes = divideSheathIntoSubgroups(
      sheathId,
      groupList,
      level,
      settings,
      options.dynamicGroupsDivision
    );
    resultNodes.push(...sheathNodes);
  });

  regularGroups.forEach(group => {
    if (group.isSingle) {
      group.cables.forEach(cable => {
        resultNodes.push(buildSingleNode(cable, level));
      });
    } else {
      resultNodes.push(buildMultipleNode(group, level, settings));
    }
  });

  return resultNodes.sort((a, b) => (a.firstIndex ?? 0) - (b.firstIndex ?? 0));
};

const buildTopLevelNode = ({
  id,
  level,
  label,
  type = 'group',
  cables = [],
  settings,
  allowSheath = true,
  groupCode,
  groupKey,
  defaultTitle,
  subgroupIndex,
  totalSubgroups,
  dynamicGroupsDivision = false
}) => {
  if (type === 'single') {
    const cable = cables[0];
    return {
      ...buildSingleNode(cable, level),
      label: getCableDisplayName(cable, settings?.lingua, settings)
    };
  }

  const resolvedLabel = defaultTitle || label;

  return {
    id,
    level,
    type,
    label: resolvedLabel,
    defaultTitle: resolvedLabel,
    groupCode,
    groupKey,
    cables,
    firstIndex: getFirstIndex(cables),
    subGroups: buildSubGroups(cables, level + 1, settings, { allowSheath, dynamicGroupsDivision }),
    subgroupIndex: subgroupIndex ?? cables?.[0]?.subgroupIndex ?? null,
    totalSubgroups: totalSubgroups ?? cables?.[0]?.totalSubgroups ?? null
  };
};

// ==================== FUNZIONE PRINCIPALE ====================

export const buildNestedCableStructure = ({
  groups = [],
  settings,
  forcedGroups = [],
  normalCables = [],
  dynamicGroupsDivision = false
}) => {
  if (groups.length > 0) {
    return groups.map(group => {
      if (group.isForcedGroup) {
        const groupName = group.groupData?.groupName || 'Forced Group';
        const subgroupIndex = group.groupData?.subgroupIndex || null;
        const totalSubgroups = group.groupData?.totalSubgroups || null;
        return buildTopLevelNode({
          id: group.key || groupName,
          level: 1,
          type: 'group',
          label: groupName,
          groupCode: groupName,
          groupKey: group.key || groupName,
          defaultTitle: groupName,
          cables: group.groupData?.allCables || [],
          settings,
          subgroupIndex,
          totalSubgroups,
          dynamicGroupsDivision
        });
      }

      const sortedGroup = group.sortedGroupData || { cables: group.cables || [] };
      const firstCable = sortedGroup.cables?.[0];
      const label = firstCable ? getCableDisplayName(firstCable, settings?.lingua, settings) : 'Group';
      const isSheath = sortedGroup.key && sortedGroup.key.startsWith('sheath-');
      const subgroupIndex = sortedGroup.cables?.[0]?.subgroupIndex || null;
      const totalSubgroups = sortedGroup.cables?.[0]?.totalSubgroups || null;
      const baseGroupCode = firstCable?.originalSiglatura || group.key;

      return buildTopLevelNode({
        id: group.key,
        level: 1,
        type: isSheath ? 'sheath' : 'group',
        label,
        cables: sortedGroup.cables || [],
        settings,
        allowSheath: !isSheath,
        groupCode: baseGroupCode,
        subgroupIndex,
        totalSubgroups,
        dynamicGroupsDivision
      });
    }).sort((a, b) => (a.firstIndex ?? 0) - (b.firstIndex ?? 0));
  }

  const nodes = [];

  forcedGroups.forEach(group => {
    if (group.isForcedGroup) {
      const groupName = group.groupData?.groupName || 'Forced Group';
      nodes.push(buildTopLevelNode({
        id: group.key || groupName,
        level: 1,
        type: 'group',
        label: groupName,
        groupCode: groupName,
        groupKey: group.key || groupName,
        defaultTitle: groupName,
        cables: group.groupData?.allCables || [],
        settings,
        subgroupIndex: group.groupData?.subgroupIndex || null,
        totalSubgroups: group.groupData?.totalSubgroups || null,
        dynamicGroupsDivision
      }));
    }
  });

  if (normalCables && normalCables.length > 0) {
    const groupMap = new Map();
    const ungroupedCables = [];

    normalCables.forEach(cable => {
      const groupCode = typeof cable.gruppo === 'string' ? cable.gruppo.trim() : '';
      if (groupCode) {
        if (!groupMap.has(groupCode)) {
          groupMap.set(groupCode, []);
        }
        groupMap.get(groupCode).push(cable);
      } else {
        ungroupedCables.push(cable);
      }
    });

    groupMap.forEach((groupCables, groupCode) => {
      const firstCable = groupCables?.[0];
      const baseGroupCode = firstCable?.originalSiglatura || groupCode;
      nodes.push(buildTopLevelNode({
        id: groupCode,
        level: 1,
        type: 'group',
        label: baseGroupCode,
        groupCode: baseGroupCode,
        groupKey: groupCode,
        defaultTitle: baseGroupCode,
        cables: groupCables,
        settings,
        dynamicGroupsDivision
      }));
    });

    if (ungroupedCables.length > 0) {
      // Per i cavi non raggruppati, usa buildSubGroups con la divisione dinamica
      const normalNodes = buildSubGroups(ungroupedCables, 1, settings, {
        allowSheath: true,
        dynamicGroupsDivision
      });
      nodes.push(...normalNodes);
    }
  }

  return nodes.sort((a, b) => (a.firstIndex ?? 0) - (b.firstIndex ?? 0));
};

